import { useState } from "react";
import { Accordion, Form, Badge } from "react-bootstrap";
import { CATEGORIES, TOOLS, RATINGS, LEVELS, DURATIONS } from "../data/Mockdata2";

// Section Wrapper (styled accordion item)
function FilterSection({ eventKey, title, children }) {
    return (
        <Accordion.Item eventKey={eventKey} className="border rounded-0">
            <Accordion.Header>
                {title}
            </Accordion.Header>
            <Accordion.Body className={`${title} ${title === "Category" ? "p-0" : "p-3"}`}>{children}</Accordion.Body>
        </Accordion.Item>
    );
}

// Checkbox Row
function CheckRow({ id, label, count, active, onChange }) {
    return (
        <div className="d-flex align-items-center justify-content-between py-1">
            <Form.Check
                type="checkbox"
                id={id}
                label={
                    <span className={`${active ? "text-primary" : "text-dark text-opacity-75"}`}>
                        {label}
                    </span>
                }
                checked={active}
                onChange={onChange}
                className="mb-0"
            />
            {count !== undefined && (
                <span className={`text-xs ${active ? "text-body" : "text-secondary"}`}>{count}</span>
            )}
        </div>
    );
}

// Star Row (rating filter)
function StarRow({ id, label, value, active, onChange }) {
    return (
        <div className="d-flex align-items-center justify-content-between py-1">
            <Form.Check
                type="radio"
                name="rating"
                id={id}
                checked={active}
                onChange={onChange}
                label={
                    <span className="d-flex align-items-center gap-1">
                        {Array.from({ length: 1 }).map((_, i) => (
                            <i key={i} className={`bi bi-star-fill`}
                                style={{
                                    color: i < value ? "#F5A623" : "#D1D5DB",
                                }}
                            />
                        ))}
                        <span className={active ? "text-primary" : "text-dark text-opacity-75"}>{label}</span>
                    </span>
                }
                className="mb-0"
            />
            <span className={`text-xs ${active ? "text-body" : "text-secondary"}`}>1345</span>
        </div>
    );
}

// SidebarFilters
export default function SidebarFilters() {
    const [minPrice, setMinPrice] = useState(0);
    const [maxPrice, setMaxPrice] = useState(200);

    // Filter states
    const [activeCategoryId, setActiveCategoryId] = useState("mobile-dev");
    const [selectedTools, setSelectedTools] = useState(["webflow"]);
    const [selectedRating, setSelectedRating] = useState("3star");
    const [selectedLevels, setSelectedLevels] = useState([]);
    const [selectedDurations, setSelectedDurations] = useState(["1-3mo"]);
    const [selectedPrices, setSelectedPrices] = useState([]);
    const [selectedSubCategories, setSelectedSubCategories] = useState(["mobile-dev"]);

    // Handlers
    const toggleSubCategory = (id) => {
        setSelectedSubCategories(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
    };
    const togglePrice = (id) => {
        setSelectedPrices(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
    };
    const toggleTool = (id) => {
        setSelectedTools(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);
    };

    const toggleLevel = (lvl) => {
        const id = `lvl-${lvl}`;
        setSelectedLevels(prev => prev.includes(id) ? prev.filter(l => l !== id) : [...prev, id]);
    };

    const toggleDuration = (id) => {
        setSelectedDurations(prev => prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]);
    };

    return (
        <Accordion className="accordion-wrapper d-flex flex-column gap-3 small" defaultActiveKey={["category", "tools", "rating", "level", "price", "duration"]} alwaysOpen flush>

            {/* CATEGORY */}
            <FilterSection eventKey="category" title="Category">
                <Accordion flush className="nested-accordion" defaultActiveKey={CATEGORIES[0]?.id}>
                    {CATEGORIES.map((cat, index) => (
                        cat.children && cat.children.length > 0 ? (
                            <Accordion.Item eventKey={cat.id} key={cat.id} className={index !== CATEGORIES.length - 1 ? "border-bottom" : ""}>
                                <Accordion.Header onClick={() => setActiveCategoryId(cat.id)}>
                                    <div className="d-flex align-items-center gap-2">
                                        <i className={`bi ${cat.icon} ${activeCategoryId === cat.id ? "text-primary" : "text-secondary"} text-lg`} />
                                        <span className={`${activeCategoryId === cat.id ? "text-black" : "text-black"}`}>{cat.label}</span>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body>
                                    <div className="d-flex flex-column">
                                        {cat.children.map((child) => (
                                            <CheckRow
                                                key={child.id}
                                                id={child.id}
                                                label={child.label}
                                                count={child.count}
                                                active={selectedSubCategories.includes(child.id)}
                                                onChange={() => toggleSubCategory(child.id)}
                                            />
                                        ))}
                                    </div>
                                </Accordion.Body>
                            </Accordion.Item>
                        ) : (
                            <div key={cat.id} className="d-flex align-items-center gap-2 pointer border-bottom" style={{ padding: "0.75rem 1rem" }} onClick={() => setActiveCategoryId(cat.id)}>
                                <i className={`bi ${cat.icon} ${activeCategoryId === cat.id ? "text-primary" : "text-secondary"} text-lg`} />
                                <span className={`flex-grow-1 ${activeCategoryId === cat.id ? "text-primary" : "text-dark"}`}>{cat.label}</span>
                            </div>
                        )
                    ))}
                </Accordion>
            </FilterSection>

            {/* TOOLS */}
            <FilterSection eventKey="tools" title="Tools">
                {TOOLS.map((t) => (
                    <CheckRow key={t.id} id={t.id} label={t.label} count={t.count} active={selectedTools.includes(t.id)} onChange={() => toggleTool(t.id)} />
                ))}
            </FilterSection>

            {/* RATING */}
            <FilterSection eventKey="rating" title="Rating">
                {RATINGS.map((r) => (
                    <StarRow key={r.id} id={r.id} label={r.label} value={r.value} active={selectedRating === r.id} onChange={() => setSelectedRating(r.id)} />
                ))}
            </FilterSection>

            {/* COURSE LEVEL */}
            <FilterSection eventKey="level" title="Course Level">
                {LEVELS.map((lvl) => (
                    <CheckRow key={lvl} id={`lvl-${lvl}`} label={lvl} count={1345} active={selectedLevels.includes(`lvl-${lvl}`)} onChange={() => toggleLevel(lvl)} />
                ))}
            </FilterSection>

            {/* PRICE */}
            <FilterSection eventKey="price" title="Price">
                {/* Range Slider */}
                <div className="mb-3">
                    <Form.Range
                        min={0} max={500}
                        value={maxPrice}
                        onChange={(e) => setMaxPrice(Number(e.target.value))}
                        className="accent-primary"
                        style={{ accentColor: "#F5A623" }}
                    />
                    <div className="d-flex gap-2 mt-2">
                        <div className="input-group input-group-sm">
                            <span className="input-group-text bg-white">$</span>
                            <Form.Control
                                type="number" value={minPrice}
                                onChange={(e) => setMinPrice(Number(e.target.value))}
                                placeholder="min" size="sm"
                            />
                        </div>
                        <div className="input-group input-group-sm">
                            <span className="input-group-text bg-white">$</span>
                            <Form.Control
                                type="number" value={maxPrice}
                                onChange={(e) => setMaxPrice(Number(e.target.value))}
                                placeholder="max" size="sm"
                            />
                        </div>
                    </div>
                </div>
                <CheckRow id="paid" label="Paid" count={1345} active={selectedPrices.includes("paid")} onChange={() => togglePrice("paid")} />
                <CheckRow id="free" label="Free" count={1345} active={selectedPrices.includes("free")} onChange={() => togglePrice("free")} />
            </FilterSection>

            {/* DURATION */}
            <FilterSection eventKey="duration" title="Duration">
                {DURATIONS.map((d) => (
                    <CheckRow key={d.id} id={d.id} label={d.label} count={1345} active={selectedDurations.includes(d.id)} onChange={() => toggleDuration(d.id)} />
                ))}
            </FilterSection>

        </Accordion>
    );
}